/**
 * Ekran varlığı: aynı ekranı (nesneyi) açık tutan kullanıcılar ve düzenledikleri satır.
 *
 * Sunucu tarafı DAL SystemHub (JoinScreen / LeaveScreen / BeginEdit / EndEdit / NotifyRowSaved).
 * Kayıtlar SignalR bağlantısına bağlı: sekme kapanınca ya da bağlantı kopunca sunucu kaydı
 * kendiliğinden düşürür. Kilit değil, bilgilendirmedir — kayıt yine de kaydedilebilir.
 *
 * Aynı nesneyi bir sekmede birden çok bileşen kullanabilir (master grid + düzenleme formu,
 * aynı ekranın iki sekmesi): katılım sayaçla tutulur, son bileşen çıkınca ekrandan ayrılınır.
 * Bağlantı yeniden kurulunca (yeni bağlantı kimliği) katılımlar ve düzenlemeler yeniden bildirilir.
 */
import { create } from 'zustand';
import { eventBus, type PresenceUser } from '@/shared/realtime/eventBus';
import { systemHub } from '@/shared/realtime/systemHub';
import { useAuthStore } from '@/entities/session/model/authStore';

export type { PresenceUser };

interface PresenceState {
  /** nesne (küçük harf) → o ekrandaki bütün bağlantılar (bu sekme dahil) */
  byObject: Record<string, PresenceUser[]>;
}

export const usePresenceStore = create<PresenceState>(() => ({ byObject: {} }));

const norm = (id: string) => id.toLowerCase();
const joins = new Map<string, number>();
/** nesne → bu sekmede açık düzenlemeler (satır anahtarı → sayaç) */
const edits = new Map<string, Map<string, number>>();

function setUsers(objectId: string, users: PresenceUser[] | null) {
  usePresenceStore.setState((s) => {
    const next = { ...s.byObject };
    if (users) next[objectId] = users;
    else delete next[objectId];
    return { byObject: next };
  });
}

const userName = () => {
  const u = useAuthStore.getState().user;
  return u?.userName || u?.userCode || '';
};

async function sendJoin(objectId: string) {
  try {
    const users = await systemHub.call<PresenceUser[]>('JoinScreen', objectId, userName());
    if (users && joins.has(objectId)) setUsers(objectId, users);
  } catch {
    /* varlık isteğe bağlı: hata ekranı etkilemez */
  }
}

export function joinScreen(objectId: string) {
  if (!objectId) return;
  const id = norm(objectId);
  const n = joins.get(id) ?? 0;
  joins.set(id, n + 1);
  if (n === 0) void sendJoin(id);
}

export function leaveScreen(objectId: string) {
  if (!objectId) return;
  const id = norm(objectId);
  const n = joins.get(id) ?? 0;
  if (n <= 1) {
    joins.delete(id);
    setUsers(id, null);
    void systemHub.invoke('LeaveScreen', id).catch(() => {});
  } else joins.set(id, n - 1);
}

/**
 * Satır düzenlemesini bildirir. Dönen liste: sunucuya göre aynı satırı ZATEN düzenleyen
 * diğer bağlantılar (yerel liste henüz güncellenmemişse uyarı buradan verilir).
 */
export async function beginEdit(objectId: string, rowKey: string): Promise<PresenceUser[]> {
  if (!objectId || !rowKey) return [];
  const id = norm(objectId);
  joinScreen(id);
  const rows = edits.get(id) ?? new Map<string, number>();
  edits.set(id, rows);
  rows.set(rowKey, (rows.get(rowKey) ?? 0) + 1);
  try {
    // Katılım isteği uçuştaysa sunucu düzenlemeyi kaydedemez: önce katılım tamamlansın.
    if ((joins.get(id) ?? 0) === 1) await sendJoin(id);
    return (await systemHub.call<PresenceUser[]>('BeginEdit', id, rowKey)) ?? [];
  } catch {
    return [];
  }
}

export function endEdit(objectId: string, rowKey: string) {
  if (!objectId || !rowKey) return;
  const id = norm(objectId);
  const rows = edits.get(id);
  const n = rows?.get(rowKey) ?? 0;
  if (rows && n > 1) rows.set(rowKey, n - 1);
  else {
    rows?.delete(rowKey);
    if (rows && rows.size === 0) edits.delete(id);
    void systemHub.invoke('EndEdit', id, rowKey).catch(() => {});
  }
  leaveScreen(id);
}

/** Kayıt kaydedildi: aynı satırı düzenlemekte olan diğer kullanıcılar uyarılır. */
export function notifyRowSaved(objectId: string, rowKey: string | null | undefined) {
  if (!objectId || !rowKey) return;
  void systemHub.invoke('NotifyRowSaved', norm(objectId), rowKey).catch(() => {});
}

/**
 * Veri değişikliğini aynı ekranı açık tutan diğer bağlantılara duyurur (işlem/barkod SP'si
 * sonrası — kayıt/silme uçları bunu sunucuda kendisi yapar). Anahtarsız → tam yenileme.
 */
export function notifyRowsChanged(objectId: string, keys: string[] | null) {
  if (!objectId) return;
  const list = (keys ?? []).filter(Boolean);
  void systemHub
    .invoke('NotifyRowsChanged', norm(objectId), list.length ? 'update' : 'refresh', list)
    .catch(() => {});
}

/** Bu sekme dışındaki bağlantılar (aynı kullanıcının başka sekmeleri dahil). */
export function othersOf(users: PresenceUser[] | undefined): PresenceUser[] {
  const me = systemHub.connectionId();
  return (users ?? []).filter((u) => u.connectionId !== me);
}

/** Satırı şu an düzenleyen diğer bağlantılar (yerel listeden, sunucuya sormadan). */
export function rowEditors(objectId: string, rowKey: string): PresenceUser[] {
  const users = usePresenceStore.getState().byObject[norm(objectId)];
  return othersOf(users).filter((u) => u.editingKey === rowKey);
}

eventBus.on('ScreenPresence', ({ objectId, users }) => {
  const id = norm(objectId);
  if (joins.has(id)) setUsers(id, users);
});

// Yeniden bağlanınca bağlantı kimliği değişir; sunucudaki eski kayıtlar düşmüştür.
eventBus.on('ConnectionState', (state) => {
  if (state !== 'connected') return;
  for (const id of joins.keys()) {
    void sendJoin(id).then(() => {
      for (const rowKey of edits.get(id)?.keys() ?? [])
        void systemHub.invoke('BeginEdit', id, rowKey).catch(() => {});
    });
  }
});

/** Testler için: modül durumunu sıfırlar. */
export function __resetPresence() {
  joins.clear();
  edits.clear();
  usePresenceStore.setState({ byObject: {} });
}
